<template>
  <div>
    <f7-link @click="isPopupOpened = true">
      <f7-icon
        icon="icon-download"
      >
        <f7-badge
          v-if="badgeNewUpdate > 0"
          color="red"
        >
          {{ badgeNewUpdate }}
        </f7-badge>
      </f7-icon>
    </f7-link>

    <f7-popup v-model:opened="isPopupOpened">
      <f7-page>
        <template #fixed>
          <TheNavbar
            :nav-title="t('appUpdate.common.appUpdate')"
            :popup-close-btn-enable="true"
          />
        </template>

        <f7-list class="no-margin-top">
          <f7-list-item :title="t('appUpdate.common.currentVersion')">
            <template #after>
              {{ `${appVersion} (${minErpVersion} - ${BUILD_TIMESTAMP})` }}
            </template>
          </f7-list-item>

          <f7-list-item
            v-if="newAppVersionData"
            :title="t('appUpdate.common.newVersion')"
          >
            <template #after>
              {{ formattedNewVersion }}
            </template>
          </f7-list-item>

          <f7-list-item :title="t('appUpdate.common.lastCheck')">
            <template #after>
              {{ t('appUpdate.common.lastCheckDate', { date: formattedLastCheckDate }) }}
            </template>
          </f7-list-item>
        </f7-list>

        <f7-block v-if="newAppVersionData">
          <f7-button
            fill
            class="margin-top"
            @click="handleDownload"
          >
            {{ t('common.download') }}
          </f7-button>
        </f7-block>

        <f7-block
          v-else
          class="display-flex justify-content-center"
        >
          <f7-block-title>
            {{ t('appUpdate.common.youHaveTheLatestVersion') }}
          </f7-block-title>
        </f7-block>
      </f7-page>
    </f7-popup>
  </div>
</template>

<script setup>
// @ts-check
import { computed, ref, watch } from 'vue';
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { useI18n } from 'vue-i18n';
import { logError } from '@/js/utils/log';
import { BUILD_TIMESTAMP } from '@/js/common/constants';

import TheNavbar from './TheNavbar.vue';

/** @import { Ref } from 'vue' */
/** @import { ConfiguracionDistribucionAPP } from '@erpagroapp/types' */

const props = defineProps({
  appVersion: {
    type: String,
    default: null,
  },
  appName: {
    type: String,
    default: null,
  },
  preloaderDialogMessage: {
    type: String,
    default: null,
  },
  newVersionEndpoint: {
    type: Function,
    default: null,
  },
  baseApiDownloadUrl: {
    type: String,
    required: true,
  },
  minErpVersion: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

/** @type {Ref<number>} */
const lastCheckDate = ref(0);
/** @type {Ref<number>} */
const badgeNewUpdate = ref(0);
/** @type {Ref<boolean>} */
const isPopupOpened = ref(false);

/** @type {Ref<ConfiguracionDistribucionAPP | null>} */
const newAppVersionData = ref(null);

const formattedNewVersion = computed(() => {
  if (!newAppVersionData.value) {
    return null;
  }

  const version = newAppVersionData.value.VersionApp;
  return `${version.Major}.${version.Minor}.${version.Revision}`;
});

const formattedLastCheckDate = computed(() => {
  const date = new Date(lastCheckDate.value);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
});

watch(isPopupOpened, () => {
  if (isPopupOpened.value) {
    checkNewAvailableAppVersion(false);
  }
});

/**
 * Check for a new version of the application.
 * @param {boolean} inBackground - `true` if the check is in background, `false` otherwise.
 * @returns {Promise<void>}
 */
async function checkNewAvailableAppVersion(inBackground) {
  if (!inBackground) {
    const preloaderDialogMessage = props.preloaderDialogMessage ?? t('appUpdate.info.checkingNewUpdates');
    HiDialog.Preloader(true, preloaderDialogMessage);
  }

  try {
    const { data } = await props.newVersionEndpoint(props.appName, props.appVersion);

    if (data.Valor) {
      badgeNewUpdate.value = 1;
      newAppVersionData.value = data.Tag;
    } else {
      badgeNewUpdate.value = 0;
      newAppVersionData.value = null;
    }
  } finally {
    if (!inBackground) {
      HiDialog.Preloader(false);
    }

    lastCheckDate.value = new Date().getTime();
  }
}

function showInstallationAlertError() {
  const alertTitle = t('common.warning');
  const alertText = t('appUpdate.info.errorDownloadingNewVersion');
  HiNotification.Alert(alertText, alertTitle);
}

async function handleDownload() {
  if (!newAppVersionData.value) {
    return;
  }

  const apkUrl = `${props.baseApiDownloadUrl}/${newAppVersionData.value.RutaDescarga}`;
  const sandBoxDirectory = window.cordova.file.dataDirectory;

  const fileName = apkUrl.match(/[^/]+$/)?.[0];
  if (!fileName) {
    throw new Error('File name not found');
  }

  HiDialog.Preloader(true, t('appUpdate.info.downloadingNewVersion'));

  try {
    const response = await fetch(apkUrl);
    if (!response.ok) {
      throw new Error('Failed to download APK');
    }

    const blob = await response.blob();

    window.resolveLocalFileSystemURL(sandBoxDirectory, (entry) => {
      const dirEntry = /** @type {DirectoryEntry} */ (entry);
      dirEntry.getFile(fileName, { create: true, exclusive: false }, (fileEntry) => {
        fileEntry.createWriter((fileWriter) => {
          fileWriter.onwriteend = () => {
            // Install the app
            // @ts-expect-error
            // eslint-disable-next-line no-undef
            apkInstaller.install(
              fileName,
              () => {
                HiDialog.Preloader(false);
              },
              () => {
                HiDialog.Preloader(false);
                showInstallationAlertError();
              },
            );
          };

          fileWriter.onerror = () => {
            HiDialog.Preloader(false);
            showInstallationAlertError();
          };

          fileWriter.write(blob);
        });
      });
    });
  } catch (error) {
    HiDialog.Preloader(false);
    logError(error);
    showInstallationAlertError();
  }
}

defineExpose({
  checkNewAvailableAppVersion,
});
</script>

<style scoped>
.icon-download {
  --f7-badge-padding: 0 4px;

  background-image: url("@/static/icons/ic_download_gray.svg");
  width: 32px;
  height: 32px;
}
</style>
